import { useEffect, useState } from "react";
import { Copy, ClipboardCheck, RefreshCw, Braces, Eye, EyeOff, AlertOctagon, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TAB_META = {
  just_listed: { label: "JUST LISTED" },
  price_reduction: { label: "PRICE REDUCTION" },
  sold: { label: "SOLD" },
};

export default function StylerOutput({ result, onRegenerate, generating, brand }) {
  const templates = result.templates || [];
  const [active, setActive] = useState(templates[0]?.type);
  const [pretty, setPretty] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showChanges, setShowChanges] = useState(false);

  useEffect(() => { if (!templates.find((t) => t.type === active)) setActive(templates[0]?.type); }, [templates, active]);
  useEffect(() => { setCopied(false); }, [active, result]);

  const current = templates.find((t) => t.type === active);
  const jsonText = current?.json ? (pretty ? JSON.stringify(current.json, null, 2) : JSON.stringify(current.json)) : "";

  const copy = async () => {
    if (!current?.valid || !jsonText) return;
    try {
      await navigator.clipboard.writeText(jsonText);
    } catch (_) {
      const ta = document.createElement("textarea");
      ta.value = jsonText; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success(`${TAB_META[current.type].label} JSON copied — paste into Creatomate`);
    setTimeout(() => setCopied(false), 2200);
  };

  const ch = current?.changes || {};
  const mb = current?.master_brand || {};
  const roleRows = [
    ["Primary colour", mb.primary, brand.primary],
    ["Accent colour", mb.accent, brand.accent],
    ["Neutral colour", mb.neutral, brand.neutral],
    ["Hero font", mb.hero_font, brand.hero_font],
    ["Support font", mb.support_font, brand.support_font],
  ];

  return (
    <section className="ts-panel ts-output" id="styler-output" data-testid="styler-output">
      <div className="ts-out-tabs" data-testid="styler-output-tabs">
        {templates.map((t) => (
          <button key={t.type} className={`ts-out-tab ${active === t.type ? "active" : ""} ${t.valid ? "" : "invalid"}`} onClick={() => setActive(t.type)} data-testid={`styler-tab-${t.type}`}>
            <span>{TAB_META[t.type]?.label || t.type}</span>
            {t.valid ? <CheckCircle2 size={14} /> : <AlertOctagon size={14} />}
            <small>master v{t.master_version}</small>
          </button>
        ))}
      </div>

      {current && !current.valid && (
        <div className="ts-invalid" data-testid="styler-validation-failed">
          <AlertOctagon size={18} />
          <div>
            <strong>Template generation failed validation.</strong>
            <p>{TAB_META[current.type]?.label} could not be produced safely — nothing has been copied.</p>
            <ul>{current.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
          </div>
        </div>
      )}

      {current && current.valid && (
        <>
          <div className="ts-out-bar">
            <div className="ts-out-stats" data-testid="styler-out-stats">
              <span><b>{ch.color_replacements ?? 0}</b> colour replacements</span>
              <span><b>{ch.font_replacements ?? 0}</b> font replacements</span>
              {ch.contrast?.length > 0 && <span className="warn"><b>{ch.contrast.length}</b> contrast fixes</span>}
              <span className="ok"><CheckCircle2 size={13} /> Valid RenderScript</span>
            </div>
            <div className="ts-out-btns">
              <button className="ts-btn ghost sm" onClick={() => setPretty(!pretty)} data-testid="styler-format-json"><Braces size={14} /> {pretty ? "Compact" : "Format JSON"}</button>
              <button className="ts-btn ghost sm" onClick={() => setShowChanges(!showChanges)} data-testid="styler-show-changes">{showChanges ? <EyeOff size={14} /> : <Eye size={14} />} {showChanges ? "Hide changes" : "Show changes"}</button>
              <button className="ts-btn ghost sm" onClick={onRegenerate} disabled={generating} data-testid="styler-regenerate">{generating ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />} Regenerate</button>
              <button className={`ts-btn primary sm ${copied ? "copied" : ""}`} onClick={copy} data-testid="styler-copy-json">{copied ? <ClipboardCheck size={15} /> : <Copy size={15} />} {copied ? "✓ COPIED" : "COPY JSON"}</button>
            </div>
          </div>

          {showChanges && (
            <div className="ts-changes" data-testid="styler-changes">
              <h4>{TAB_META[current.type]?.label} — what changed</h4>
              <div className="ts-changes-grid">
                {roleRows.map(([label, from, to]) => (
                  <div key={label} className="ts-change-row">
                    <span className="ts-change-label">{label}</span>
                    <span className="ts-change-vals">
                      {from?.startsWith("#") && <i style={{ background: from }} />}<code>{from || "—"}</code>
                      <span className="ts-arrow">→</span>
                      {to?.startsWith("#") && <i style={{ background: to }} />}<code>{to || "—"}</code>
                    </span>
                  </div>
                ))}
              </div>
              {ch.contrast?.length > 0 && (
                <div className="ts-changes-list">
                  <h5>Contrast safety</h5>
                  {ch.contrast.map((c, i) => (
                    <div key={i} className="ts-change-line warn">
                      <b>{c.element}</b> {c.from} on {c.background} was {c.ratio}:1 → {c.applied ? `swapped to ${c.suggested}${c.suggested_role && !c.suggested_role.startsWith('#') ? ` (${c.suggested_role})` : ''} (${c.suggested_ratio}:1)` : `suggest ${c.suggested} (not applied)`}
                    </div>
                  ))}
                </div>
              )}
              {ch.warnings?.length > 0 && (
                <div className="ts-changes-list">
                  <h5>Notes</h5>
                  {ch.warnings.map((w, i) => <div key={i} className="ts-change-line">{w}</div>)}
                </div>
              )}
              <details className="ts-details">
                <summary>Element-level log ({(ch.colors?.length || 0) + (ch.fonts?.length || 0)} edits)</summary>
                {(ch.colors || []).map((c, i) => <div key={`c${i}`} className="ts-change-line"><b>{c.element}</b> {c.role}: <code>{c.from}</code> → <code>{c.to}</code></div>)}
                {(ch.fonts || []).map((c, i) => <div key={`f${i}`} className="ts-change-line"><b>{c.element}</b> {c.role} font: <code>{c.from}</code> → <code>{c.to}</code></div>)}
              </details>
            </div>
          )}

          <pre className="ts-json" data-testid="styler-json"><code>{jsonText}</code></pre>
        </>
      )}
    </section>
  );
}
